/**
 * Case Study Slider - Embla Carousel Implementation
 * 3D parallax effect on desktop, skew effect on mobile
 * Based on original implementation with boundingClientRect calculation
 */

if (!customElements.get('case-study-slider')) {
  class CaseStudySlider extends HTMLElement {
    constructor() {
      super();
      this.embla = null;
      this.viewport = null;
      this.prevButton = null;
      this.nextButton = null;
      this.autoplayInterval = null;
      this.isDragging = false;

      // Settings from data attributes
      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.speed) || 5000;
    }

    connectedCallback() {
      this.viewport = this.querySelector('.case-study-slider__viewport');
      this.prevButton = this.querySelector('.case-study-slider__button-prev');
      this.nextButton = this.querySelector('.case-study-slider__button-next');

      if (!this.viewport) return;

      // Wait for Embla to load
      this.initWhenReady();
    }

    initWhenReady() {
      if (typeof EmblaCarousel !== 'undefined') {
        this.initEmbla();
      } else {
        // Retry after a short delay
        setTimeout(() => this.initWhenReady(), 50);
      }
    }

    initEmbla() {
      const options = {
        loop: true,
        align: 'center',
        containScroll: false
      };

      const plugins = [EmblaCarouselClassNames()];

      this.embla = EmblaCarousel(this.viewport, options, plugins);

      // Set aria-labels on slides (replaces Liquid forloop)
      this.updateAriaLabels();

      // Bind events
      this.embla.on('select', this.onSelect.bind(this));
      this.embla.on('scroll', this.updateInViewProgress.bind(this));
      this.embla.on('reInit', this.updateInViewProgress.bind(this));

      // Initial progress calculation
      requestAnimationFrame(() => this.updateInViewProgress());

      // Navigation buttons
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.embla.scrollPrev());
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.embla.scrollNext());
      }

      // Keyboard navigation
      this.addEventListener('keydown', this.handleKeydown.bind(this));

      // Autoplay
      if (this.autoplay) {
        this.startAutoplay();

        // Stop on hover
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => {
          if (!this.isDragging) this.startAutoplay();
        });

        // Stop on focus
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('focusout', () => {
          if (!this.isDragging) this.startAutoplay();
        });

        // Stop during drag interaction (fixes artifact bug)
        this.embla.on('pointerDown', () => {
          this.isDragging = true;
          this.classList.add('is-dragging');
          this.stopAutoplay();
        });
        this.embla.on('pointerUp', () => {
          this.isDragging = false;
          this.classList.remove('is-dragging');
        });
        this.embla.on('settle', () => {
          // Restart autoplay after drag settles (if not hovered)
          if (!this.isDragging && !this.matches(':hover') && !this.contains(document.activeElement)) {
            this.startAutoplay();
          }
        });
      }

      // Theme editor support
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this.initEditorEvents();
      }

      this.updateButtons();
    }

    onSelect() {
      this.updateButtons();
    }

    /**
     * Set aria-labels on slides based on their position
     * Replaces Liquid forloop.index / forloop.length
     */
    updateAriaLabels() {
      if (!this.embla) return;

      const slides = this.embla.slideNodes();
      const total = slides.length;

      slides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `${index + 1} of ${total}`);
      });
    }

    /**
     * Calculate --inViewProgress based on boundingClientRect
     * Original formula from the reference implementation
     */
    updateInViewProgress() {
      if (!this.embla) return;

      const slides = this.embla.slideNodes();
      if (!slides.length) return;

      const sliderRect = this.viewport.getBoundingClientRect();

      slides.forEach((slide) => {
        const card = slide.querySelector('.case-study-slider__card');
        if (!card) return;

        const slideRect = slide.getBoundingClientRect();

        // Original formula: position relative to slider viewport
        let inViewProgress = ((slideRect.left - sliderRect.left) + slideRect.width)
                            / (sliderRect.width + slideRect.width);

        // Clean up floating point
        if (Math.abs(inViewProgress) < Number.EPSILON * 2) {
          inViewProgress = 0.0;
        }

        // Normalize to -1 to 1 range with rounding
        inViewProgress = Math.min(1, Math.max(-1,
          Math.round((inViewProgress + Number.EPSILON) * 200000 - 100000) / 100000
        ));

        card.style.setProperty('--inViewProgress', inViewProgress);
      });
    }

    updateButtons() {
      if (!this.embla) return;

      // With loop enabled, buttons are always active
      if (this.prevButton) {
        this.prevButton.disabled = !this.embla.canScrollPrev();
      }
      if (this.nextButton) {
        this.nextButton.disabled = !this.embla.canScrollNext();
      }
    }

    handleKeydown(e) {
      if (!this.embla) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.embla.scrollPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.embla.scrollNext();
      }
    }

    startAutoplay() {
      if (this.autoplayInterval) return;

      this.autoplayInterval = setInterval(() => {
        if (this.embla) {
          this.embla.scrollNext();
        }
      }, this.autoplaySpeed);

      // Set aria-live to off during autoplay
      const track = this.querySelector('.case-study-slider__track');
      if (track) track.setAttribute('aria-live', 'off');
    }

    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }

      // Set aria-live to polite when paused
      const track = this.querySelector('.case-study-slider__track');
      if (track) track.setAttribute('aria-live', 'polite');
    }

    initEditorEvents() {
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target.contains(this)) {
          this.reinitialize();
        }
      });

      document.addEventListener('shopify:section:unload', (e) => {
        if (e.target.contains(this)) {
          this.destroy();
        }
      });

      document.addEventListener('shopify:block:select', (e) => {
        const blockId = this.dataset.blockId;
        if (e.detail.blockId === blockId) {
          this.stopAutoplay();

          // Find slide index and scroll to it
          const slides = this.embla?.slideNodes() || [];
          const slideIndex = slides.findIndex(slide => slide.contains(e.target));

          if (slideIndex !== -1 && this.embla) {
            this.embla.scrollTo(slideIndex);
          }
        }
      });

      document.addEventListener('shopify:block:deselect', (e) => {
        const blockId = this.dataset.blockId;
        if (e.detail.blockId === blockId && this.autoplay) {
          this.startAutoplay();
        }
      });

      document.addEventListener('shopify:inspector:activate', () => {
        this.stopAutoplay();
      });

      document.addEventListener('shopify:inspector:deactivate', () => {
        if (this.autoplay) {
          this.startAutoplay();
        }
      });
    }

    reinitialize() {
      this.destroy();
      this.viewport = this.querySelector('.case-study-slider__viewport');
      this.prevButton = this.querySelector('.case-study-slider__button-prev');
      this.nextButton = this.querySelector('.case-study-slider__button-next');

      if (this.viewport) {
        this.initEmbla();
      }
    }

    destroy() {
      this.stopAutoplay();
      if (this.embla) {
        this.embla.destroy();
        this.embla = null;
      }
    }

    disconnectedCallback() {
      this.destroy();
    }
  }

  customElements.define('case-study-slider', CaseStudySlider);
}
